<template>
  <RouterLink
    :to="`/event/${eventItem.id}`"
    :class="[
      'bg-white rounded-md hover:opacity-80 flex flex-col delay-200 duration-500 transition border-surface-grey border',
      list ? 'md:flex-row' : '',
      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
    ]"
    @click="handleProductClick"
  >
    <div
      ref="inviewRef"
      :id="`event-${eventItem.id}`"
      :class="[
        'aspect-16/9 border-b border-surface-grey relative',
        list
          ? 'md:shrink-0 md:w-90 h-full md:aspect-auto md:rounded-l-md md:rounded-r-none md:border-b-0 md:border-r'
          : '',
      ]"
    >
      <img
        :src="photoToUrl(eventItem.photo)"
        :alt="eventItem.info1"
        :class="[
          'w-full h-full object-cover rounded-t-md aspect-16/9',
          list ? 'md:rounded-l-md md:rounded-t-none md:rounded-r-none' : '',
        ]"
      />
      <div 
        v-if="badges[0]" 
        :class="'absolute top-2 right-2 text-headings text-xs px-2 py-1 rounded font-semibold ' + badges[0].class"
      >
        <template v-if="badges[0].localeKey">
          {{ $t(badges[0].localeKey) }}
        </template>
        <template v-else>
          {{ badges[0].string || badges[0] }}
        </template>
      </div>
      <svg
        :class="[
          'absolute z-10 bottom-0 -right-[1px] translate-y-1/2 fill-surface-light stroke-surface-grey stroke-1',
          list
            ? 'md:rotate-90 md:translate-y-0 md:-translate-x-full md:-bottom-[1px] md:right-0 md:origin-bottom-right'
            : '',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
      >
        <path
          d="M7.5 0.518555C3.59105 0.775917 0.5 4.02593 0.5 8C0.5 11.9741 3.59105 15.2241 7.5 15.4814V0.518555Z"
        />
      </svg>

      <svg
        :class="[
          'absolute bottom-0 -left-[1px] z-10 translate-y-1/2 fill-surface-light stroke-surface-grey stroke-1',
          list
            ? 'md:rotate-90 md:translate-x-full md:-top-[2px] md:left-auto md:bottom-auto md:-right-[1px] md:origin-top-right'
            : '',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
        fill="none"
      >
        <path
          d="M-0.5 0.518555C3.40895 0.775917 6.5 4.02593 6.5 8C6.5 11.9741 3.40895 15.2241 -0.5 15.4814V0.518555Z"
        />
      </svg>
    </div>
    <div :class="['p-4 grow', list ? '' : '']">
      <div
        :class="[
          list
            ? 'md:flex md:justify-between md:border-b md:border-surface-grey md:mb-3'
            : '',
        ]"
      >
        <div :class="list ? 'md:flex-1 ' : ''">
          <h2
            class="event-title h4 mb-2"
            :class="eventNameVisibilityClass"
          >
            {{ getLocalizedEventValue(eventItem, locale, 'info1') }}
          </h2>
          <p
            v-if="eventItem.info2"
            class="text-text text-sm mb-4 whitespace-pre-line"
            :class="eventNameVisibilityClass"
          >
            {{ getLocalizedEventValue(eventItem, locale, 'info2') }}
          </p>
        </div>
        <div :class="list ? 'md:text-right md:min-w-42 md:ml-4' : ''">
          <div
            :class="[
              'flex flex-col mb-4',
              priceVisibilityClass,
              list ? 'md:items-end' : '',
            ]"
          >
            <!-- With an sso price it is shown above in the regular price's style, and the regular price becomes muted -->
            <span
              v-if="ssoPriceText"
              class="text-lg font-bold text-headings wrap-anywhere"
              >{{ ssoPriceText }}</span
            >
            <div
              :class="[
                'flex items-center gap-2',
                list ? 'md:justify-end md:flex-col md:items-end' : '',
              ]"
            >
              <span :class="[regularPriceClass, 'wrap-anywhere']">{{ regularPriceText }}</span>
              <span
                v-if="priceBefore"
                class="text-sm text-headings line-through font-semibold text-text"
                >{{ $t('common.before') }}
                {{
                  formatPrice(
                    priceBefore,
                    eventItem.currency,
                    $i18n.locale
                  )
                }}
                </span
              >
            </div>
          </div>
        </div>
      </div>
      <div
        :class="dateVisibilityClass"
        class="flex items-center text-sm text-headings mb-2"
      >
        <CalendarIcon class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
        <EventDate :event="eventItem" :showTime="generalStore.customer().frontpage_show_time" class="line-clamp-1" />
      </div>
      <div
        :class="venueVisibilityClass"
        class="flex items-center text-sm text-headings"
      >
        <MapPin class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
        <span class="line-clamp-1">{{ venue ? venue.name : '' }}</span>
      </div>
      <div
        :class="categoryVisibilityClass"
        class="mt-3 flex flex-wrap gap-2"
      >
        <span
          v-for="(tag, i) in categoryNames"
          :key="i"
          class="inline-block max-w-[200px] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs px-2 py-1 font-semibold rounded border-1 border-surface-grey bg-surface-light text-text"
        >{{ tag }}</span>
      </div>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { useGeneralStore } from '@/stores/general';
  import { useSsoStore } from '@/stores/sso';
  import type { EventItem } from '@/types';
  import { trackProductClick } from '@/utils/analytics/ecommerce';
  import { getBadgesWithVisibility, getCategoryVisibilityClass, getDateVisibilityClass, getEventNameVisibilityClass, getLocalizedEventValue, getPriceVisibilityClass, getVenueVisibilityClass } from '@/utils/eventItemUtil';
  import { formatPrice } from '@/utils/formatPrice';
  import { getLocalizedCategoryValue } from '@/utils/getCategoryLocalization';
  import { photoToUrl } from '@/utils/photoToUrl';
  import { useInView } from '@/utils/useInView';
  import { useSsoLabels } from '@/utils/useSsoLabels';

  import CalendarIcon from '../icons/CalendarIcon.vue';
  import MapPin from '../icons/MapPinIcon.vue';
  import EventDate from './EventDate.vue';

  const { locale, t: $t  } = useI18n();
  
  const props = defineProps<{
    eventItem: EventItem;
    list?: boolean;
  }>();
  
  const { inviewRef, inView } = useInView();

  const generalStore = useGeneralStore();
  const ssoStore = useSsoStore();
  const { ssoPriceLabel, regularPriceLabel } = useSsoLabels();

  function handleProductClick() {
    trackProductClick(props.eventItem);
  }

  const venues = computed(() => generalStore.venues());

  const venue = computed(() =>
    venues.value?.find(v => v.id === props.eventItem.venue)
  );

  const categoryNames = computed(() => {
    if (generalStore.customer().hide_categories_everywhere) return [];

    const categoryIds = props.eventItem.categories || [];
    const allCategories = generalStore.categories();

    return categoryIds.reduce((names, id) => {
      const category = allCategories.find(category => category.id === id);
      // filter out categories that are not available in the active language
      if (category && category.localizations?.[locale.value]?.['name']) {
        names.push(getLocalizedCategoryValue(category, locale.value, 'name'));
      }
      return names;
    }, [] as string[]);
  });

  const isExternalTicketsale = computed(() =>
    generalStore.customer().external_ticketsale === 1 && !!props.eventItem.external_ticketsale_url
  );

  const badges = computed(() => getBadgesWithVisibility(props.eventItem, { externalTicketsaleActive: isExternalTicketsale.value }));
  
  const priceBefore = computed(() => {
    return null;
    
  }); 

  // The sso price is shown next to the normal price, regardless of login. The override text replaces it (also for
  // external ticketsale, where sso_prices is not used).
  const ssoPriceText = computed<string | null>(() => {
    if (!ssoStore.isActive) {
      return null;
    }
    if (props.eventItem.single_sign_on_price_override_active) {
      // The override replaces the sso price completely, even when its text is empty (nothing is shown then).
      return getLocalizedEventValue(props.eventItem, locale.value, 'single_sign_on_price_override_text');
    }
    if (isExternalTicketsale.value || !ssoStore.memberUsertype) {
      return null;
    }
    const price = props.eventItem.sso_prices?.[ssoStore.memberUsertype]?.price;
    if (price === null || price === undefined) {
      return null;
    }
    return `${ssoPriceLabel.value} ${formatPrice(price, props.eventItem.currency, locale.value)}`;
  });

  // The regular price text. The override has priority over the external ticketsale price, as in the old client.
  // With an sso price it gets a prefix, unless it is the organizer's override text (a full phrase already).
  const regularPriceText = computed(() => {
    if (props.eventItem.price_override_active) {
      return getLocalizedEventValue(props.eventItem, locale.value, 'price_override_text');
    }
    const text = isExternalTicketsale.value && props.eventItem.external_ticketsale_price
      ? formatPrice(props.eventItem.external_ticketsale_price, props.eventItem.currency, locale.value)
      : formatPrice(props.eventItem.price, props.eventItem.currency, locale.value);
    return ssoPriceText.value ? `${regularPriceLabel.value} ${text}` : text;
  });

  // With an sso price the regular price is muted (the style the sso price had before), otherwise it looks as it always did.
  const regularPriceClass = computed(() => ssoPriceText.value ? 'text-sm text-text font-semibold' : 'text-lg font-bold text-headings');

  const categoryVisibilityClass = computed(() => getCategoryVisibilityClass(props.eventItem));
  const dateVisibilityClass = computed(() => getDateVisibilityClass(props.eventItem));
  const eventNameVisibilityClass = computed(() => getEventNameVisibilityClass(props.eventItem, props.list));
  const venueVisibilityClass = computed(() => getVenueVisibilityClass(props.eventItem));
  const priceVisibilityClass = computed(() => getPriceVisibilityClass(props.eventItem));
</script>
